import { ARTICLES, articlesInSection, latest } from "@/data/articles";
import type { SectionId } from "@/data/sections";
import { sectionById } from "@/data/sections";
import { Link, useDocumentTitle } from "@/lib/router";
import { CardStory, LeadStory, ListStory, SectionHeader, articleHref, Img } from "@/components/Story";
import { EmiCalculator, Newsletter, Trending } from "@/components/Sidebar";

function SectionBlock({ id }: { id: SectionId }) {
  const items = articlesInSection(id).filter(a => !a.video);
  const [first, ...rest] = items;
  if (!first) return null;
  return (
    <section>
      <SectionHeader title={sectionById(id)!.label} href={`/section/${id}`} />
      <CardStory a={first} size="md" />
      <div className="mt-2">
        {rest.slice(0, 3).map(a => (
          <ListStory key={a.slug} a={a} thumb={false} />
        ))}
      </div>
    </section>
  );
}

export function Home() {
  useDocumentTitle("BrickBharat | India's Real Estate News");

  const all = latest().filter(a => !a.video && a.section !== "opinion");
  const lead = ARTICLES[0]!;
  const rest = all.filter(a => a.slug !== lead.slug);
  const left = rest.slice(0, 4);
  const right = rest.slice(4, 7);
  const topGrid = rest.slice(7, 11);
  const videos = ARTICLES.filter(a => a.video);
  const opinion = articlesInSection("opinion");

  return (
    <main className="mx-auto max-w-[1240px] px-4 pt-5">
      {/* Front page */}
      <div className="grid gap-6 lg:grid-cols-12">
        <div className="order-2 lg:order-1 lg:col-span-3 lg:border-r lg:border-rule lg:pr-6">
          <CardStory a={left[0]!} size="sm" />
          <div className="mt-1">
            {left.slice(1).map(a => (
              <ListStory key={a.slug} a={a} />
            ))}
          </div>
        </div>
        <div className="order-1 lg:order-2 lg:col-span-6">
          <LeadStory a={lead} />
        </div>
        <div className="order-3 space-y-6 lg:col-span-3 lg:border-l lg:border-rule lg:pl-6">
          <Trending />
        </div>
      </div>

      {/* Second band */}
      <div className="mt-8 grid gap-6 border-t-[3px] border-ink pt-5 sm:grid-cols-2 lg:grid-cols-4">
        {[...right, ...topGrid].slice(0, 4).map(a => (
          <CardStory key={a.slug} a={a} size="sm" />
        ))}
      </div>

      {/* Sections + sidebar */}
      <div className="mt-10 grid gap-8 lg:grid-cols-12">
        <div className="grid gap-8 sm:grid-cols-2 lg:col-span-9">
          <SectionBlock id="residential" />
          <SectionBlock id="commercial" />
          <SectionBlock id="policy" />
          <SectionBlock id="infrastructure" />
        </div>
        <aside className="space-y-8 lg:col-span-3">
          <EmiCalculator />
          <Newsletter />
        </aside>
      </div>

      {/* Video band */}
      <section className="-mx-4 mt-12 bg-ink px-4 py-8 text-white">
        <div className="mx-auto max-w-[1240px]">
          <div className="mb-4 flex items-end justify-between border-t-[6px] border-brand pt-2">
            <h2 className="splash text-[2rem]">
              BrickBharat <span className="text-brand">Video</span>
            </h2>
            <Link href="/video" className="text-sm font-extrabold uppercase tracking-wide hover:text-brand">
              See all ›
            </Link>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {videos.map(a => (
              <Link key={a.slug} href={articleHref(a)} className="story block">
                <Img src={a.image} alt={a.title} className="aspect-video" video />
                <h3 className="headline mt-2 text-[1.05rem]">{a.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Luxury + cities + finance */}
      <div className="mt-10 grid gap-8 md:grid-cols-3">
        <SectionBlock id="luxury" />
        <SectionBlock id="cities" />
        <SectionBlock id="finance" />
      </div>

      {/* Opinion */}
      <section className="mt-10">
        <SectionHeader title="Opinion" href="/section/opinion" />
        <div className="grid gap-6 md:grid-cols-2">
          {opinion.map(a => (
            <Link key={a.slug} href={articleHref(a)} className="story flex gap-4 border-b border-rule pb-4">
              <span className="flex size-16 shrink-0 items-center justify-center rounded-full bg-brand font-[family-name:var(--font-masthead)] text-2xl font-black text-white">
                {a.author.split(" ").map(p => p[0]).join("")}
              </span>
              <div>
                <p className="kicker">{a.author}</p>
                <h3 className="headline mt-0.5 text-[1.35rem]">{a.title}</h3>
                <p className="mt-1 font-[family-name:var(--font-body)] text-[15px] text-ink-soft">{a.dek}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Latest */}
      <section className="mt-10">
        <SectionHeader title="The Latest" />
        <div className="grid gap-x-8 md:grid-cols-2 lg:grid-cols-3">
          {latest()
            .slice(0, 9)
            .map(a => (
              <ListStory key={a.slug} a={a} />
            ))}
        </div>
      </section>
    </main>
  );
}
